import { useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { Bell, CheckCheck, Loader2 } from "lucide-react"
import { formatDistanceToNow } from "date-fns"

import type { components } from "@/api/v1"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"
import {
  useMarkAllReadMutation,
  useMarkNotificationReadMutation,
  useNotificationsInfiniteQuery,
  useUnreadCountQuery,
} from "../queries"

type Notification = components["schemas"]["StaffNotification"]
type PatientTab = "adt-events" | "chats"

const getNotificationTargetTab = (notification: Notification): PatientTab =>
  notification.notificationType === "chatbot_alert" ? "chats" : "adt-events"

export function NotificationBell() {
  const [open, setOpen] = useState(false)
  const { data: unreadData } = useUnreadCountQuery()
  
  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useNotificationsInfiniteQuery()
  const markRead = useMarkNotificationReadMutation()
  const markAllRead = useMarkAllReadMutation()
  const navigate = useNavigate()

  const unreadCount = unreadData?.unreadCount ?? 0
  const notifications: Notification[] =
    data?.pages.flatMap(p => p.results) ?? []

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead && notification.id != null) {
      markRead.mutate(notification.id)
    }

    if (notification.patient) {
      const targetTab = getNotificationTargetTab(notification)
      setOpen(false)
      void navigate({
        to: "/manage/patients/$patientId/",
        params: { patientId: String(notification.patient) },
        search: { tab: targetTab } as never,
      })
    }
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="relative size-9">
          <Bell className="size-5" />
          {unreadCount > 0 && (
            <span className="absolute right-1 top-1 flex size-4 items-center justify-center rounded-full bg-danger text-[10px] font-bold leading-none text-white">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="flex w-96 flex-col p-0">
        <SheetHeader className="flex-row items-center justify-between border-b border-border px-4 py-3">
          <SheetTitle className="text-base">Notifications</SheetTitle>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="mr-6 h-7 gap-1.5 text-xs text-muted-foreground"
              onClick={() => markAllRead.mutate()}
              disabled={markAllRead.isPending}
            >
              <CheckCheck className="size-3.5" />
              Mark all read
            </Button>
          )}
        </SheetHeader>
        <ScrollArea className="flex-1">
          {isLoading ? (
            <div className="space-y-1 p-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex gap-3 rounded-lg p-3">
                  <Skeleton className="mt-1.5 size-2 shrink-0 rounded-full" />
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-3.5 w-3/4" />
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-2.5 w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
              <Bell className="size-8 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">No notifications</p>
            </div>
          ) : (
            <div className="p-2">
              <div className="space-y-0.5">
                {notifications.map(notification => (
                  <button
                    key={notification.id}
                    className={cn(
                      "flex w-full cursor-pointer gap-3 rounded-lg p-3 text-left transition-colors hover:bg-muted/60",
                      !notification.isRead && "bg-cta/5 hover:bg-cta/10",
                    )}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <span
                      className={cn(
                        "mt-1.5 size-2 shrink-0 rounded-full",
                        notification.isRead ? "bg-transparent" : "bg-cta",
                      )}
                    />
                    <div className="min-w-0 flex-1">
                      <p
                        className={cn(
                          "text-sm",
                          !notification.isRead && "font-semibold",
                        )}
                      >
                        {notification.title}
                      </p>
                      <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                        {notification.body}
                      </p>
                      {notification.createdAt && (
                        <p className="mt-1 text-[11px] text-muted-foreground/70">
                          {formatDistanceToNow(new Date(notification.createdAt), {
                            addSuffix: true,
                          })}
                        </p>
                      )}
                    </div>
                  </button>
                ))}
              </div>

              {hasNextPage && (
                <div className="mt-2 flex justify-center pb-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-full text-xs text-muted-foreground"
                    onClick={() => void fetchNextPage()}
                    disabled={isFetchingNextPage}
                  >
                    {isFetchingNextPage ? (
                      <Loader2 className="size-3.5 animate-spin" />
                    ) : (
                      "Load more"
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  )
}
